import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import classNames from 'classnames'
import * as React from 'react'
import type {SyntheticEvent} from 'react'

import {ROOT_PROJECT_ID} from '../../../../types'
import IconButton from '../../../common/IconButton/IconButton'
import Popup from '../../../common/Popup/Popup.lazy'
import type {
  Entity,
  HolderType,
  Owner,
  PolicyEntry,
  PolicyHolder,
} from '../CleanupProjectPage.types'
import {getPolicyHolder} from '../CleanupProjectPage.utils'

import ActionsSection from './ActionsSection/ActionsSection'
import {isPolicyCustom, shouldPreventClickOnRow} from './Listing.utils'

import styles from './Listing.policies.css'

type OwnProps = {
  className?: string
  policy: PolicyEntry
  type: HolderType
  owner: Owner
  readOnly: boolean
  inherited: boolean
  onPolicyReset: (arg0: PolicyHolder) => unknown
  onPolicyClick: (arg0: Entity) => unknown
  onPolicyDisable: (arg0: PolicyHolder, arg1: boolean) => unknown
}

function ListingPolicies({
  policy,
  type,
  owner,
  onPolicyReset,
  onPolicyClick,
  onPolicyDisable,
  readOnly,
  inherited,
}: OwnProps) {
  const {everythingPolicy, historyPolicy, artifactsPolicy, options, externalId, internalId} = policy
  const policyClickHandler = React.useCallback(() => {
    onPolicyClick({
      policy,
      type,
      owner,
    })
  }, [onPolicyClick, owner, policy, type])
  const allowReset = React.useMemo(
    () => isPolicyCustom(policy) && externalId !== ROOT_PROJECT_ID,
    [policy, externalId],
  )
  const onRowClick = React.useCallback(
    (event: SyntheticEvent) => {
      if (shouldPreventClickOnRow(event)) {
        return
      } else {
        policyClickHandler()
      }
    },
    [policyClickHandler],
  )
  const holder = React.useMemo(() => getPolicyHolder(internalId, type), [internalId, type])
  const resetHandler = React.useCallback(() => {
    if (allowReset) {
      onPolicyReset(holder)
    }
  }, [allowReset, onPolicyReset, holder])
  const disableHandler = React.useCallback(
    (value: boolean) => onPolicyDisable(holder, value),
    [holder, onPolicyDisable],
  )
  return (
    <div
      className={classNames(styles.container, {
        [styles.disabled]: policy.options.cleanupPoliciesDisabled,
        [styles.inheritedSection]: inherited,
        [styles.editable]: !readOnly,
      })}
      role={'presentation'} // click handler duplicates the one of Edit button
      onClick={readOnly ? undefined : onRowClick}
    >
      <div className={styles.wrapper}>
        <h3 className={styles.caption}>
          {'Base rule'}
          <Dropdown
            hoverMode
            hoverShowTimeOut={0}
            anchor={<IconButton className={styles.helpIcon} icon="help" title="" />}
          >
            <Popup className={styles.helpPopup}>
              {
                'Base\u00a0rules define policies on\u00a0data deletion during the\u00a0clean-up. They are easy to\u00a0configure and cover general clean-up\u00a0cases.'
              }
            </Popup>
          </Dropdown>
        </h3>
        <div className={classNames(styles.policiesContainer, styles.policies)}>
          <div className={styles.policyListing}>
            {everythingPolicy && (
              <div
                className={classNames(
                  styles.policy,
                  !everythingPolicy.ownPolicy && styles.inherited,
                )}
              >
                {`Everything ${everythingPolicy.policyDescription}`}
              </div>
            )}
            {historyPolicy && (
              <div
                className={classNames(styles.policy, !historyPolicy.ownPolicy && styles.inherited)}
              >
                {`History ${historyPolicy.policyDescription}`}
              </div>
            )}
            {artifactsPolicy && (
              <div
                className={classNames(
                  styles.policy,
                  !artifactsPolicy.ownPolicy && styles.inherited,
                )}
              >
                {`Artifacts ${artifactsPolicy.policyDescription}`}
              </div>
            )}
            {(!everythingPolicy || !historyPolicy || !artifactsPolicy) && (
              <div
                className={classNames(
                  styles.policy,
                  externalId !== ROOT_PROJECT_ID && styles.inherited,
                )}
              >
                {`${
                  !everythingPolicy && !historyPolicy && !artifactsPolicy
                    ? 'Everything is '
                    : 'Other items are '
                } kept forever`}
              </div>
            )}
            <div className={classNames(!options.ownPrevent && styles.inherited)}>{`${
              options.preventDependenciesArtifactsFromCleanup ? 'Prevent' : 'Do not prevent'
            } dependency artifacts cleanup`}</div>
          </div>
          {!readOnly && (
            <ActionsSection
              className={styles.actions}
              policy={policy}
              type={type}
              onResetClick={allowReset ? resetHandler : null}
              onPolicyDisable={disableHandler}
              onEditClick={policyClickHandler}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ListingPolicies
